/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Action, State } from '@table-library/react-table-library/common';
import { Table, Data, Header, HeaderRow, HeaderCell, Body, Row, Cell, TableNode } from '@table-library/react-table-library/table';
import { CellTree, TreeExpandClickTypes, useTree } from '@table-library/react-table-library/tree';
import { CellSelect, SelectTypes, useRowSelect } from '@table-library/react-table-library/select';
import G17Client from '../../../services/G17Client';
import { CustomReportSubsidiarySurvey } from '../../../types/custom-report';
import { DATE, formatDateUTC, getMonthNonJSOptions, getPeriodOptions } from '../../../utils/date';
import { getPeriodName } from '../../../utils/universalTracker';
import { DataPeriods } from '../../../types/universalTracker';
import { SurveyType } from '../../../types/survey';
import { BasicAlert } from '../../alert/BasicAlert';
import { useTheme } from '@table-library/react-table-library/theme';
import { useIsMounted } from '../../../hooks/useIsMounted';
import Loader from '../../loader';
import { useAppDispatch } from '../../../reducers';
import { addSiteError } from '../../../slice/siteAlertsSlice';
import { naturalSort } from '../../../utils';
import { getSurveyTypeOptions } from '../../../utils/survey';
import { FilterSelect } from './FilterSelect';
import { SurveyFilters } from '../../../routes/admin-dashboard/components/SubsidiaryUserDelegation/SubsidiaryDelegationTable';

export interface TableNodeWithSurveyIds extends TableNode {
  name: string;
  period: DataPeriods;
  effectiveDate: string;
  surveyCount: number;
  surveyType: SurveyType;
}

interface Filters {
  surveyType?: SurveyType;
  period?: DataPeriods;
  month?: string;
  year?: string;
}

export type FilterId = keyof Filters;

export interface SurveySelectionTableProps {
  initiativeId: string;
  initiativeIds: string[];
  selectedNodeIds: string[];
  handleSelectedNodes: (selectedNodes?: TableNodeWithSurveyIds[]) => void;
  isMulti?: boolean;
  enabledFilters?: FilterId[];
  defaultFilters?: Filters;
}

const buildTree = (list: CustomReportSubsidiarySurvey[], filters: Filters): Data => {
  const yearMap = new Map<string, Map<SurveyType, Map<string, CustomReportSubsidiarySurvey[]>>>();

  list.forEach((survey) => {
    const { period, type = SurveyType.Default } = survey;

    const monthYearPeriod = `${survey.effectiveDate} ${period}`;

    const year = formatDateUTC(survey.effectiveDate, DATE.YEAR_ONLY);
    if (!yearMap.has(year)) {
      yearMap.set(year, new Map());
    }
    const surveyTypeMap = yearMap.get(year);
    if (!surveyTypeMap) return;

    if (!surveyTypeMap.has(type)) {
      surveyTypeMap.set(type, new Map());
    }

    const monthYearMap = surveyTypeMap.get(type);
    if (!monthYearMap) return;

    if (!monthYearMap.has(monthYearPeriod)) {
      monthYearMap.set(monthYearPeriod, []);
    }

    const monthYearList = monthYearMap.get(monthYearPeriod);
    if (!monthYearList) return;

    monthYearList.push(survey);
  });

  const data: Data = {
    nodes: [],
  };

  yearMap.forEach((surveyTypeMap, year) => {
    const yearNodes: TableNode[] = [];

    if (filters.year && filters.year !== year) {
      return;
    }

    surveyTypeMap.forEach((monthYearMap, surveyType) => {
      if (filters.surveyType && filters.surveyType !== surveyType) {
        return;
      }
      const periodNodes: TableNodeWithSurveyIds[] = [];

      monthYearMap.forEach((monthYearList, monthYearPeriod) => {
        const currentDate = monthYearList[0].effectiveDate;
        if (filters.month && filters.month !== formatDateUTC(currentDate, 'M')) {
          return;
        }

        const [effectiveDate, period] = monthYearPeriod.split(' ');
        if (filters.period && filters.period !== period) {
          return;
        }
        const monthYear = formatDateUTC(effectiveDate, DATE.MONTH_YEAR);
        periodNodes.push({
          id: `${year}-${surveyType}-${monthYear}-${period}`,
          name: `${monthYear} ${getPeriodName(period as DataPeriods, false)}`,
          effectiveDate: currentDate,
          period: period as DataPeriods,
          periodName: `${getPeriodName(period as DataPeriods, false)}`,
          surveyCount: monthYearList.length,
          surveyType,
        });
      });

      if (periodNodes.length === 0) {
        return;
      }

      yearNodes.push({
        id: `${year}-${surveyType}`,
        name: surveyType === SurveyType.Default ? 'Surveys' : 'Combined surveys',
        nodes: periodNodes,
      });
    });

    if (yearNodes.length === 0) {
      return;
    }

    data.nodes.push({
      id: year,
      name: year,
      nodes: yearNodes,
    });
  });

  // Sort DESC by year
  data.nodes.sort((a, b) => naturalSort(b.id, a.id));

  return data;
};

const findNodesByIds = (tableData: Data, ids?: string[]): TableNodeWithSurveyIds[] => {
  if (!ids || ids.length === 0) {
    return [];
  }
  let nodesMatch: TableNodeWithSurveyIds[] = [];
  tableData.nodes.forEach((year) =>
    year.nodes?.forEach((period) => {
      const matches = period.nodes?.filter((monthYear) => ids.includes(monthYear.id)) as TableNodeWithSurveyIds[];
      if (matches.length > 0) {
        nodesMatch.push(...matches);
      }
    })
  );
  return nodesMatch;
};

const getInitialFilters = (defaultFilter: Filters | undefined): Filters => ({
  surveyType: undefined,
  period: undefined,
  month: undefined,
  year: undefined,
  ...defaultFilter,
});

interface SurveyListState {
  data: CustomReportSubsidiarySurvey[],
  loaded: boolean;
}

export const SurveySelectionTable = (props: SurveySelectionTableProps) => {
  const {
    initiativeId,
    initiativeIds,
    selectedNodeIds,
    handleSelectedNodes,
    isMulti = false,
    enabledFilters = Object.values(SurveyFilters),
    defaultFilters,
  } = props;

  const [{ data: surveyList, loaded }, setSurveyList] = useState<SurveyListState>({ loaded: false, data: [] });
  const [filters, setFilters] = useState<Filters>(getInitialFilters(defaultFilters));
  const tableData = useMemo(() => buildTree(surveyList, filters), [surveyList, filters]);
  const dispatch = useAppDispatch();

  const isMounted = useIsMounted();

  const toggleFilter = (key: keyof Filters, value: string) => {
    setFilters((state) => ({
      ...state,
      [key]: (state[key] === value) ? undefined : value
    }));
  }

  useEffect(() => {
    if (!initiativeIds || initiativeIds.length === 0) {
      return handleSelectedNodes();
    }
    G17Client.customReportGetSurveysByInitiativeIds(initiativeId, initiativeIds, filters.surveyType)
      .then((surveyList) => {
        if (isMounted()) {
          setSurveyList({ loaded: true, data: surveyList });
        }
      })
      .catch(e => {
        setSurveyList({ loaded: true, data: [] });
        dispatch(addSiteError(e));
      });
  }, [handleSelectedNodes, initiativeId, initiativeIds, filters.surveyType, isMounted, dispatch]);

  const expandedIds = useMemo(() => {
    if (selectedNodeIds.length) {
      return selectedNodeIds.reduce((ids, curNodeId) => {
        const parts = curNodeId.split('-');
        // Must be 4 parts, `${year}-${surveyType}-${monthYear}-${period}`
        return parts.length === 4 ? [...ids, parts[0], `${parts[0]}-${parts[1]}`] : ids;
      }, [] as string[]);
    }

    const firstNode = tableData.nodes[0]?.id;
    return firstNode ? [firstNode] : [];
  }, [selectedNodeIds, tableData.nodes]);

  const theme = useTheme({
    Table: `
      height: unset;
      max-height: 100%;
      --data-table-library_grid-template-columns: 50% repeat(1, minmax(0, 2fr)) repeat(1, minmax(0, 1fr));
    `,
  });

  const tree = useTree(tableData,
    { state: { ids: expandedIds } },
    {
      treeIcon: {
        margin: '4px',
        iconDefault: <i className='fa mx-2' />,
        iconRight: <i className='fa fa-caret-right text-ThemeAccentExtradark mx-2' />,
        iconDown: <i className='fa fa-caret-down text-ThemeAccentExtradark mx-2' />,
      },
      clickType: TreeExpandClickTypes.RowClick,
    }
  );

  const select = useRowSelect(tableData,
    {
      state: { ids: selectedNodeIds },
      onChange: isMulti ? onMultipleSelectChange : onSelectChange,
    },
    {
      rowSelect: isMulti ? SelectTypes.MultiSelect : SelectTypes.SingleSelect,
      buttonSelect: isMulti ? SelectTypes.MultiSelect : SelectTypes.SingleSelect,
    }
  );

  const selectedNodes = findNodesByIds(tableData, selectedNodeIds);

  const updateSelectedNode = (ids: string[]) => {
    const selectedNodes = findNodesByIds(tableData, ids);
    handleSelectedNodes(selectedNodes);
  };

  function onMultipleSelectChange(action: Action, state: State) {
    return updateSelectedNode(state.ids ?? []);
  }

  function onSelectChange(action: Action, state: State) {
    return updateSelectedNode(state.id ? [state.id] : []);
  }

  const yearOptions = useMemo(() => {
    const years = Array.from(
      new Set(surveyList.map((survey) => formatDateUTC(survey.effectiveDate, DATE.YEAR_ONLY))).values()
    ).sort();
    return years.map((year) => ({ label: year, value: year }));
  }, [surveyList]);

  const surveyFilters = {
    [SurveyFilters.SurveyType]: {
      label: 'Survey Type',
      options: getSurveyTypeOptions(),
    },
    [SurveyFilters.Period]: {
      label: 'Monthly/Quarterly/Annually',
      options: getPeriodOptions(),
    },
    [SurveyFilters.Month]: {
      label: 'Month',
      options: getMonthNonJSOptions().map((option) => ({ ...option, value: String(option.value) })),
    },
    [SurveyFilters.Year]: {
      label: 'Year',
      options: yearOptions,
    },
  };
  
  return (
    <>
      <div className='d-flex align-items-center'>
        {enabledFilters.length > 0 ?<div className='mr-3 strong'>
          Filters:
        </div>: null}
        {enabledFilters.map((filter: keyof Filters) => (
          <FilterSelect
            key={filter}
            placeholder={surveyFilters[filter].label}
            options={surveyFilters[filter].options}
            value={filters[filter as keyof Filters]}
            onSelectChange={(value) => toggleFilter(filter, value)}
          />
        ))}
        <div className='ml-auto'>
          {`${selectedNodes.reduce((acc, node) => acc + node.surveyCount, 0) ?? 0} surveys selected`}
        </div>
      </div>
      <div className='mt-2 collapsible-table'
        style={{
          height: '400px',
        }}>
        {!loaded ? <Loader /> :
          tableData.nodes.length > 0 ?
          <Table data={tableData} tree={tree} select={select} theme={theme} layout={{ fixedHeader: true, custom: true }}>
            {(tableList) => (
              <>
                <Header>
                  <HeaderRow>
                    <HeaderCell>Survey</HeaderCell>
                    <HeaderCell>Subsidiaries with survey</HeaderCell>
                    <HeaderCell />
                  </HeaderRow>
                </Header>

                <Body>
                  {tableList.map((item) => (
                    <Row key={item.id} item={item}>
                      <CellTree item={item}>{item.name}</CellTree>
                      <Cell>{item.surveyCount}</Cell>
                      {Array.isArray(item.nodes) ? <Cell /> : <CellSelect item={item} />}
                    </Row>
                  ))}
                </Body>
              </>
            )}
          </Table>
          :
          <BasicAlert className='mt-3' type='warning'>No surveys available for the selected subsidiaries</BasicAlert>
        }
      </div>
    </>
  );
};
